import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';

const DashboardEventTable = ({
  title,
  items,
  onRowClick,
  columns,
  emptyText,
  palette = {},
  loading,
  headerDragClassName,
  showColumnHeaders = false,
  tableLayout = 'fixed'
}) => (
  <Paper
    sx={{
      p: 0,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden'
    }}
  >
    <Box
      className={headerDragClassName || undefined}
      sx={{
        px: 1.25,
        py: 0.55,
        backgroundColor: palette.headerBg || '#1976d2',
        ...(palette.showHeaderAccent !== false
          ? { borderTop: `3px solid ${palette.accent || palette.headerBg || '#1976d2'}` }
          : {}),
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
        ...(headerDragClassName
          ? {
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }
          : {})
      }}
    >
      <Typography
        variant="subtitle2"
        noWrap
        sx={{ fontWeight: 700, letterSpacing: 0.2, color: palette.headerText || '#fff' }}
      >
        {title}
      </Typography>
    </Box>
    {loading ? (
      <Box py={1.5} display="flex" justifyContent="center">
        <CircularProgress size={20} />
      </Box>
    ) : (
      <TableContainer
        sx={{
          minHeight: 0,
          overflowY: 'scroll',
          scrollbarGutter: 'stable',
          p: 1
        }}
      >
        <Table size="small" stickyHeader={showColumnHeaders} sx={{ tableLayout, width: '100%' }}>
          {showColumnHeaders ? (
            <TableHead>
              <TableRow>
                {columns.map((column, colIdx) => (
                  <TableCell
                    key={`hdr-${column.key}`}
                    sx={{
                      ...(column.width != null ? { width: column.width, maxWidth: column.width } : {}),
                      minWidth: column.minWidth || 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '0.72rem',
                      lineHeight: 1.05,
                      fontWeight: 700,
                      py: 0.2,
                      px: 0.6,
                      textAlign: column.headerAlign || 'left',
                      fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif',
                      borderBottom: '1px solid rgba(224, 224, 224, 0.85)',
                      borderRight:
                        column.hideBorderRight || colIdx >= columns.length - 1
                          ? 'none'
                          : '1px solid rgba(224, 224, 224, 0.35)',
                      color: 'text.secondary',
                      ...(showColumnHeaders
                        ? {
                            top: -13,
                            zIndex: 20,
                            backgroundColor: 'background.paper',
                            backgroundImage: 'none',
                            backgroundClip: 'padding-box',
                            borderTop: '1px solid',
                            borderTopColor: 'background.paper',
                            boxShadow: (theme) =>
                              `inset 0 -1px 0 rgba(224,224,224,0.9), 0 -13px 0 0 ${theme.palette.background.paper}`
                          }
                        : {}),
                      ...column.headerCellSx
                    }}
                  >
                    {column.header != null ? column.header : ''}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          ) : null}
          <TableBody>
            {!items.length ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{ color: 'text.secondary', py: 0.2, px: 0.6, fontSize: '0.78rem' }}
                >
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              items.map((item, idx) => (
                <TableRow
                  hover
                  key={item.id || idx}
                  onClick={() => onRowClick(item)}
                  sx={{
                    cursor: 'pointer',
                    height: 20,
                    '&:nth-of-type(odd)': {
                      backgroundColor: palette.rowAltBg || 'rgba(25, 118, 210, 0.045)'
                    },
                    '&:hover': {
                      backgroundColor: palette.hoverBg || 'rgba(25, 118, 210, 0.12)'
                    }
                  }}
                >
                  {columns.map((column, colIdx) => (
                    <TableCell
                      key={`${item.id || idx}-${column.key}`}
                      sx={{
                        ...(column.width != null ? { width: column.width, maxWidth: column.width } : {}),
                        minWidth: column.minWidth || 0,
                        whiteSpace: 'nowrap',
                        overflow: column.noTruncate ? 'visible' : 'hidden',
                        textOverflow: column.noTruncate ? 'clip' : 'ellipsis',
                        fontSize: '0.76rem',
                        lineHeight: 1.05,
                        py: 0.12,
                        px: 0.6,
                        fontFamily: '"Roboto Condensed", "Roboto", "Helvetica", "Arial", sans-serif',
                        borderBottom: '1px solid rgba(224, 224, 224, 0.7)',
                        borderRight:
                          column.hideBorderRight || colIdx >= columns.length - 1
                            ? 'none'
                            : '1px solid rgba(224, 224, 224, 0.35)',
                        ...column.cellSx
                      }}
                    >
                      <Box
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {column.render(item)}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Paper>
);

export default DashboardEventTable;
